import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import {
  BasketballEventType,
  BasketballTeamGame,
  EventHelpoutEventableType,
  SelectOption,
  UserRole,
} from '@hefe/models';
import {
  BasketballGamesSlice,
  BasketballTeamGameActions,
  CarsSlice,
  userState,
} from '@hefe/store';
import { clubHasSubscription, formatTime } from '@hefe/utils';
import {
  Button,
  Col,
  Collapse,
  Descriptions,
  Input,
  Row,
  Select,
  Space,
  Tabs,
  TimePicker,
  Typography,
} from 'antd';
import { Gutter } from 'antd/lib/grid/row';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { IoLocationOutline, IoTimeOutline } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import InlineEditInput from '../../../../core/components/inline-edit-input/inline-edit-input.component';
import EventDetailsCommentsComponent from '../../event-details/event-details-comments/event-details-comments.component';
import EventDetailsRepliesComponent from '../../event-details/event-details-replies/event-details-replies.component';
import EventDetailsTeamTasksComponent from '../../event-details/event-details-team-tasks/event-details-team-tasks.component';
import EventTeamHelpoutsComponent from '../../event-team-helpouts/event-team-helpouts.component';
import EventTeamNomineesComponent from '../../event-team-nominees/event-team-nominees.component';
import GameDetailsTeamCarpoolComponent from '../game-details-team-carpool/game-details-team-carpool.component';
import GameDetailsTeamCarsInformationComponent from '../game-details-team-cars-info/game-details-team-cars-info.component';

interface GameDetailsTeamComponentProps {
  teamDetails: BasketballTeamGame;
  homeGame: boolean;
}

export default function GameDetailsTeamComponent(
  props: GameDetailsTeamComponentProps
) {
  const { teamDetails } = props;

  const dispatch = useAppDispatch();
  const [savingCars, setSavingCars] = useState(false);
  const [loadingCars, setLoadingCars] = useState(false);
  const [savingMeetAtTime, setSavingMeetAtTime] = useState(false);
  const [savingMeetAtLocation, setSavingMeetAtLocation] = useState(false);
  const [carsInfoOpen, setCarsInfoOpen] = useState(false);

  const [nomineesModalOpen, setNomineesModalOpen] = useState(false);

  const user = useSelector(userState).user;
  const carsSelect = useSelector(CarsSlice.carsSelectSelector);

  const gutter = [16, 16] as [Gutter, Gutter];

  const userPlayers = useMemo(() => {
    const userPlayers = props.teamDetails?.user_players || [];
    return userPlayers.filter(
      (player, index, self) =>
        self.findIndex((p) => p.id === player.id) === index
    );
  }, [props.teamDetails?.user_players]);

  const userCoaches = useMemo(() => {
    const userCoaches = props.teamDetails?.user_coaches || [];
    return userCoaches.filter(
      (coach, index, self) => self.findIndex((c) => c.id === coach.id) === index
    );
  }, [props.teamDetails?.user_coaches]);

  const userCoachesAndPlayers = useMemo(() => {
    // comoine userPlayers and COaches into one array and remove all duplicates
    const userCoachesAndPlayers = userPlayers.concat(userCoaches);
    return userCoachesAndPlayers.filter(
      (user, index, self) => self.findIndex((u) => u.id === user.id) === index
    );
  }, [userPlayers, userCoaches]);

  const tabs = useMemo(() => {
    const returnValue = [];

    returnValue.push({
      key: '1',
      label: t('replies'),
      children: (
        <EventDetailsRepliesComponent
          key="EventDetailsRepliesComponent"
          allowViewReplies={teamDetails.team?.allowViewReplies === 1}
          replies={teamDetails.team?.playerReplies}
          isCoach={userCoaches.length > 0}
          eventType={BasketballEventType.GAME}
          eventId={teamDetails.game_id}
          team={teamDetails.team ?? { id: teamDetails.team_id, name: '' }}
        />
      ),
    });

    returnValue.push({
      key: '2',
      label: t('comments.plural'),
      children: (
        <EventDetailsCommentsComponent
          comments={teamDetails?.comments}
          eventType={BasketballEventType.GAME}
          eventId={teamDetails?.game_id || 0}
          teamId={teamDetails?.team_id || 0}
          users={userCoachesAndPlayers}
        />
      ),
    });

    returnValue.push({
      key: '3',
      label: t('tasks.plural'),
      children: (
        <EventDetailsTeamTasksComponent
          tasks={teamDetails?.team?.tasks}
          players={userPlayers}
          coaches={userCoaches}
          teamPlayers={teamDetails?.team?.playerReplies}
          eventType={BasketballEventType.GAME}
          eventId={teamDetails?.game_id || 0}
          homeGame={props.homeGame}
          teamId={teamDetails?.team_id || 0}
        />
      ),
    });

    if (clubHasSubscription(AuthSubscriptions.second, user)) {
      returnValue.push({
        key: '4',
        label: t('cars.carpool'),
        children: <GameDetailsTeamCarpoolComponent teamDetails={teamDetails} />,
      });
    }

    return returnValue;
  }, [
    teamDetails,
    userCoaches,
    userCoachesAndPlayers,
    userPlayers,
    props.homeGame,
    user,
  ]);

  const nominatedPlayersText = useMemo(() => {
    if (teamDetails?.nominees?.length === 0) {
      return t('nominees.all_players_nominated');
    }

    return t('nominees.number_of_nominated_players', {
      count: teamDetails?.nominees.length,
    });
  }, [teamDetails?.nominees]);

  const fetchCars = (editMode: boolean) => {
    if (!clubHasSubscription(AuthSubscriptions.second, user)) {
      return;
    }

    if (editMode) {
      setLoadingCars(true);
      dispatch(
        CarsSlice.fetchCars({
          cacheDuration: 5 * 60 * 1000,
        })
      )
        .unwrap()
        .catch(() => null)
        .finally(() => setLoadingCars(false));
    }
  };

  const updateCars = (value: number[]) => {
    setSavingCars(true);
    dispatch(
      BasketballGamesSlice.setBasketballTeamGameCars({
        gameId: teamDetails.game_id,
        data: {
          team_id: teamDetails.team_id,
          car_ids: value,
        },
      })
    )
      .unwrap()
      .finally(() => {
        setSavingCars(false);
      });
  };

  const saveMeetAtTime = (time: string) => {
    if (time.length === 0) {
      return;
    }

    const meetAt = formatTime(time);

    setSavingMeetAtTime(true);
    dispatch(
      BasketballTeamGameActions.setBasketballTeamGameMeetingAt({
        gameId: teamDetails.game_id,
        data: {
          team_id: teamDetails.team_id,
          meet_at: meetAt,
        },
      })
    )
      .unwrap()
      .finally(() => setSavingMeetAtTime(false));
  };

  const saveMeetAtLocation = (text: string) => {
    const location = text.trim();

    if (location.length === 0) {
      return;
    }

    setSavingMeetAtLocation(true);
    dispatch(
      BasketballTeamGameActions.setBasketballTeamGameMeetingAt({
        gameId: teamDetails.game_id,
        data: {
          team_id: teamDetails.team_id,
          meet_at_location: text,
        },
      })
    )
      .unwrap()
      .finally(() => setSavingMeetAtLocation(false));
  };

  const renderMeetingPoint = () => {
    const time = teamDetails?.meet_at;
    const location = teamDetails?.meet_at_location;
    const timeText = time
      ? formatTime(time, { timeInput: true })
      : t('pending');
    const locationText = location || t('pending');

    return (
      <Space direction="vertical">
        <Space>
          <IoTimeOutline />
          <InlineEditInput
            highlightingMode="self"
            component={TimePicker}
            componentProps={{
              format: 'HH:mm',
            }}
            disabled={userCoaches.length === 0}
            value={time ? dayjs(dayjs().format('YYYY-MM-DD') + ' ' + time) : ''}
            text={timeText}
            loading={savingMeetAtTime}
            onOk={(value) => saveMeetAtTime(value)}
          />
        </Space>
        <Space>
          <IoLocationOutline />
          <InlineEditInput
            highlightingMode="self"
            component={Input}
            disabled={userCoaches.length === 0}
            value={location ?? ''}
            text={locationText}
            loading={savingMeetAtLocation}
            onOk={(value) => saveMeetAtLocation(value)}
          />
        </Space>
      </Space>
    );
  };

  const renderCars = () => {
    if (props.homeGame) {
      return;
    }

    const cars = [];
    const carIds = [];
    if (teamDetails && teamDetails.cars) {
      for (const car of teamDetails.cars) {
        cars.push(`${car.manufacturer} ${car.model}`);
        carIds.push(car.id);
      }
    }
    cars.push(t('private_cars'));

    if (
      (user?.role.includes(UserRole.BB_ADMIN) ||
        user?.role.includes(UserRole.VEHICLE_ADMIN)) &&
      clubHasSubscription(AuthSubscriptions.second, user)
    ) {
      return (
        <InlineEditInput
          highlightingMode="self"
          component={Select}
          value={carIds}
          text={cars.join(', ')}
          loading={savingCars}
          onOk={(value) => updateCars(value)}
          onModeChange={(editMode) => fetchCars(editMode)}
          componentProps={{
            options: carsSelect,
            mode: 'multiple',
            showSearch: true,
            loading: loadingCars,
            filterOption: (input: string, option: SelectOption) =>
              option?.label?.toLowerCase().includes(input?.toLowerCase()),
          }}
        />
      );
    } else {
      return <Typography.Text>{cars.join(', ')}</Typography.Text>;
    }
  };

  /******************************************************************************************
   *
   *                                     MAIN RENDER
   *
   ******************************************************************************************
   */
  return (
    <Row gutter={gutter}>
      <Col xs={24}>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t('meet_at')}>
            {renderMeetingPoint()}
          </Descriptions.Item>
          {!props.homeGame &&
            clubHasSubscription(AuthSubscriptions.second, user) && (
              <Descriptions.Item label={t('cars.plural')}>
                {renderCars()}
                {teamDetails?.cars && teamDetails?.cars?.length > 0 && (
                  <Collapse
                    ghost
                    items={[
                      {
                        key: '1',
                        label: t('details'),
                        children: (
                          <GameDetailsTeamCarsInformationComponent
                            teamDetails={teamDetails}
                          />
                        ),
                      },
                    ]}
                  />
                )}
              </Descriptions.Item>
            )}
          {clubHasSubscription(AuthSubscriptions.second, user) && (
            <Descriptions.Item label={t('helpouts.title')}>
              <EventTeamHelpoutsComponent
                helpouts={teamDetails.helpouts}
                eventType={EventHelpoutEventableType.BASKETBALL_TEAM_GAME}
                eventId={teamDetails.id}
              />
            </Descriptions.Item>
          )}
          {clubHasSubscription(AuthSubscriptions.third, user) && (
            <Descriptions.Item label={t('nominees.nominated_players')}>
              <Space direction="horizontal">
                <Button
                  type="link"
                  onClick={() => setNomineesModalOpen(true)}
                  style={{ padding: 0, margin: 0 }}
                >
                  {nominatedPlayersText}
                </Button>
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Col>
      <Col xs={24}>
        <Tabs defaultActiveKey="1" items={tabs} />
      </Col>
      <EventTeamNomineesComponent
        open={nomineesModalOpen}
        onClose={() => setNomineesModalOpen(false)}
        players={teamDetails?.team?.playerReplies}
        eventId={teamDetails?.game_id}
        eventType={BasketballEventType.GAME}
        teamId={teamDetails?.team_id}
      />
    </Row>
  );
}
